import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from './app/apps/tasklist/service/auth.service';
import { LoginComponent } from './app/apps/tasklist/components/login/login.component';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, RouterModule, LoginComponent],
    template: `
        <!-- Loading State
        <div *ngIf="authService.authLoading$ | async" class="loading-container">
            <div class="loading-content">
                <i class="pi pi-spin pi-spinner" style="font-size: 2rem; color: var(--primary-color);"></i>
                <p class="loading-text">Loading...</p>
            </div>
        </div> -->

        <!-- Authenticated State -->
        <div *ngIf="!(authService.authLoading$ | async) && (authService.isLoggedIn$ | async)">
            <router-outlet></router-outlet>
        </div>
        
        <!-- Login State -->
        <div *ngIf="!(authService.authLoading$ | async) && !(authService.isLoggedIn$ | async)" class="login-container">
            <!-- Error Message Display -->
            <div *ngIf="authService.authError$ | async as error" 
                 class="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50 max-w-md">
                <strong class="font-bold">Authentication Error: </strong>
                <span class="block sm:inline">{{ error }}</span>
                <button (click)="authService.retryLogin()" 
                        class="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm ml-2">
                    Retry
                </button>
                <button (click)="authService.clearAuthError()" 
                        class="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-2 rounded text-sm ml-1">
                    ×
                </button>
            </div>
            <app-login></app-login>
        </div>
    `,
    styles: [`
        .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            background: var(--surface-ground);
        }
        
        .loading-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
        }
        
        .loading-text {
            color: var(--text-color-secondary);
            font-size: 0.875rem;
            margin: 0;
        }
        
        .login-container {
            width: 100%;
            height: 100vh;
        }
    `]
})
export class AppComponent implements OnInit {
    authService = inject(AuthService);
    private msalService = inject(MsalService);

    constructor() {
        // Handle page visibility changes to check auth status
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                // Page became visible, check if user is still authenticated
                this.checkAuthenticationStatus();
            }
        });

        // Listen for storage changes (e.g., logout in another tab)
        window.addEventListener('storage', (event) => {
            if (event.key && event.key.includes('msal')) {
                // console.log('MSAL storage changed, rechecking authentication...');
                setTimeout(() => this.checkAuthenticationStatus(), 100);
            }
        });
    }

    private checkAuthenticationStatus(): void {
        // Re-check authentication when user returns to the app
        const activeAccount = this.msalService.instance.getActiveAccount();
        if (activeAccount && this.authService.isLoggedIn$) {
            // console.log('User is still authenticated');
        } else if (!activeAccount) {
            // console.log('No active account found on visibility change');
        }
    }

    async ngOnInit() {
        try {
            // console.log('App component initializing...');
            
            // Initialize MSAL first
            // console.log('Initializing MSAL...');
            await this.msalService.instance.initialize();
            // console.log('MSAL initialized successfully');
            
            // Handle redirect promises
            try {
                const response = await this.msalService.instance.handleRedirectPromise();
                if (response && response.account) {
                    // console.log('Setting active account from redirect:', response.account.username);
                    this.msalService.instance.setActiveAccount(response.account);
                }
            } catch (redirectError) {
                console.debug('Redirect handling completed');
            }
            
            // Now initialize AuthService
            // console.log('Initializing AuthService...');
            await this.authService.initialize();
            // console.log('AuthService initialized successfully');
            
        } catch (error) {
            console.error('Application initialization error:', error);
            // Ensure loading state is cleared even on error
            setTimeout(() => {
                // Force clear loading state as fallback
                // console.log('Clearing loading state as fallback');
            }, 3000);
        }
    }
}
